// @ts-nocheck

import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  BarChart3, 
  DollarSign, 
  FileText,
  ChevronRight,
  Target,
  TrendingUp,
  Ticket,
  Package
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { userHasAnyRole } from '@/lib/userRoles';
import Navbar from '@/components/common/Navbar';
import { supabase } from '@/lib/supabase';

const AffiliateDashboardLayout = () => {
  const location = useLocation();
  const { user, profile } = useAuth();
  const [hasAffiliateRole, setHasAffiliateRole] = React.useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = React.useState(true);
  
  console.log('AffiliateDashboardLayout rendering - Profile:', profile);
  console.log('AffiliateDashboardLayout - Current location:', location.pathname);
  
  const menuItems = [
    {
      title: "Dashboard",
      href: "/affiliate-dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      title: "Connected Athletes",
      href: "/affiliate-dashboard/athletes",
      icon: <Users className="h-5 w-5" />,
    },
    {
      title: "Analytics",
      href: "/affiliate-dashboard/analytics",
      icon: <BarChart3 className="h-5 w-5" />,
    },
    {
      title: "Coupons",
      href: "/affiliate-dashboard/coupons",
      icon: <Ticket className="h-5 w-5" />,
    },
    {
      title: "Marketing Materials",
      href: "/affiliate-dashboard/marketing",
      icon: <Package className="h-5 w-5" />,
    },
    {
      title: "Payouts",
      href: "/affiliate-dashboard/payouts",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      title: "W-9 Form",
      href: "/affiliate-dashboard/w9",
      icon: <FileText className="h-5 w-5" />,
    }
  ];

  // Function to check if the navItem is active
  const isActive = (path: string) => {
    if (path === '/affiliate-dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Check if user has affiliate role assignment
  React.useEffect(() => {
    const checkAffiliateAccess = async () => {
      if (!user?.id) {
        setIsCheckingAccess(false);
        return;
      }

      try {
        // Authorization comes from user_roles, never from profiles.role or
        // the client-writable membership slug.
        const allowed = await userHasAnyRole(user.id, ['affiliate', 'admin']);
        setHasAffiliateRole(allowed);
      } catch (err) {
        console.error('Error checking affiliate access:', err);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAffiliateAccess();
  }, [user?.id]);

  // Access requires an affiliate (or admin) assignment in user_roles.
  const hasAccess = hasAffiliateRole;

  console.log('AffiliateDashboardLayout - Access check:', {
    hasAffiliateRole,
    hasAccess,
    isCheckingAccess
  });

  if (!profile || isCheckingAccess) {
    console.log('AffiliateDashboardLayout - No profile found or checking access, showing loading');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Loading...</h1>
          <p className="text-gray-600">Loading your affiliate dashboard...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    console.log('AffiliateDashboardLayout - Access denied');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You need an affiliate account to access this dashboard.</p>
          <p className="text-sm text-gray-500 mt-2">Current role: {profile?.role || 'None'}</p>
        </div>
      </div>
    );
  }

  console.log('AffiliateDashboardLayout - Rendering dashboard layout');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full">
      {/* Top Header Bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-secondary mr-3" />
            <h1 className="text-2xl font-bold text-primary-700 font-heading">
              Affiliate Dashboard
            </h1>
          </div>
          
          {/* Removed duplicate Navbar to avoid double header */}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 w-full">
        {/* Sidebar */}
        <aside className="bg-white border-r border-gray-200 w-64 shadow-sm">
          <div className="flex flex-col h-full">
            <nav className="flex-1 overflow-y-auto py-4">
              <ul className="space-y-1 px-3">
                {menuItems.map((item) => (
                  <li key={item.title}>
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center py-2 px-3 rounded-md text-sm transition-colors",
                        isActive(item.href)
                          ? "bg-secondary/20 text-secondary-foreground hover:bg-secondary/30"
                          : "text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      <div className="flex items-center justify-start w-full">
                        {item.icon}
                        <span className="ml-3 font-medium">{item.title}</span>
                        {isActive(item.href) && (
                          <ChevronRight className="h-4 w-4 ml-auto text-secondary" />
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-500">
                Affiliate Portal v1.0
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-hidden">
          <div className="h-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AffiliateDashboardLayout;